"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ProvisionCompatibilitySelection = exports.ProvisionCompositionCandidate = exports.ProvisionCompositionCandidateKind = exports.ProvisionCompositionReason = exports.ProvisionCanonicalComposition = exports.ProvisionCanonicalSoftwareProjection = exports.ProvisionManagedSoftwareComponent = exports.ProvisionCanonicalSoftwareComponent = exports.ProvisionCanonicalHardwareProjection = exports.ProvisionManagedHardwareBinding = exports.ProvisionCanonicalHardwareBinding = exports.ProvisionEndpointInventorySnapshot = exports.ProvisionObservedComponent = exports.ProvisionObservedComponentPackageEvidence = exports.ProvisionObservationStatus = exports.ProvisionEndpointPortObservation = exports.ProvisionEndpointPortKind = exports.ProvisionComponentVersionManifest = exports.ProvisionComponentCertification = exports.ProvisionPeripheralPackageManifest = exports.ProvisionPeripheralPackageTarget = exports.ProvisionPeripheralReleaseIdentity = exports.ProvisionPeripheralRuntimeContract = exports.ProvisionPeripheralHealthContract = exports.ProvisionPeripheralLifecycleContract = exports.ProvisionPeripheralConfigurationContract = exports.ProvisionPeripheralSecretSlot = exports.ProvisionPeripheralRpcContract = exports.ProvisionPeripheralRpcInterface = exports.ProvisionPeripheralLogicalRole = exports.ProvisionPeripheralPackageManifestVersion = exports.ProvisionComponentArtifactReference = exports.ProvisionComponentDependency = exports.ProvisionSoftwareComponentIdentity = exports.ProvisionComponentType = exports.ProvisionEquipmentModelRevision = exports.ProvisionEquipmentComponentCompatibility = exports.ProvisionEquipmentDiscoveryMatch = exports.ProvisionEquipmentConnectionProfile = exports.ProvisionConnectionField = exports.ProvisionCompositionProfileVersion = exports.ProvisionEquipmentRole = exports.ProvisionCompositionCondition = exports.ProvisionEquipmentRoleRequirement = exports.ProvisionCompositionApplicationType = exports.ProvisionCompositionSchemaVersion = exports.ProvisionCompositionKey = exports.ProvisionCompositionJsonObject = exports.ProvisionCompositionJsonValue = exports.PROVISION_COMPOSITION_SCHEMA_VERSION = void 0;
exports.ProvisionCompositionDrift = exports.ProvisionCompositionDraftApplyResult = exports.ProvisionCompositionDraftApplyRequest = exports.ProvisionCompositionDraftPreview = exports.ProvisionCompositionPortClaimPreview = exports.ProvisionCompositionExpectedRecord = exports.ProvisionCompositionExpectedRecordKind = exports.ProvisionCompositionHistoryEntry = exports.ProvisionCompositionDraftPreviewRequest = exports.ProvisionCompositionDraft = exports.ProvisionCompositionDraftUpsertRequest = exports.ProvisionCompositionDraftContent = exports.ProvisionCompositionAuthoringMode = exports.ProvisionCompositionValidation = exports.ProvisionCompositionValidationRequest = exports.ProvisionCompositionFinding = exports.ProvisionLegacyMatchProposal = exports.ProvisionPeripheralManualRecord = exports.ProvisionPeripheralManualBinding = exports.ProvisionRawFieldDifference = exports.ProvisionRawFieldState = exports.ProvisionCompositionCandidateResponse = exports.ProvisionCompositionCandidateRequest = void 0;
exports.satisfiesProvisionVersionRange = satisfiesProvisionVersionRange;
exports.isProvisionVersionRangeValid = isProvisionVersionRangeValid;
exports.isProvisionSemanticVersion = isProvisionSemanticVersion;
const zod_1 = require("zod");
const terminal_js_1 = require("./terminal.js");
/**
 * Contract version for the additive terminal-composition surface.
 *
 * Version 1 deliberately keeps the deployed raw hardware fields in the
 * canonical projection. Catalogue identifiers enrich those fields; they do
 * not replace them.
 */
exports.PROVISION_COMPOSITION_SCHEMA_VERSION = 1;
/** JSON-only extension values used by lossless Advanced/manual records. */
exports.ProvisionCompositionJsonValue = zod_1.z.lazy(() => zod_1.z.union([
    zod_1.z.string(),
    zod_1.z.number().finite(),
    zod_1.z.boolean(),
    zod_1.z.null(),
    zod_1.z.array(exports.ProvisionCompositionJsonValue),
    zod_1.z.record(zod_1.z.string(), exports.ProvisionCompositionJsonValue),
]));
exports.ProvisionCompositionJsonObject = zod_1.z.record(zod_1.z.string(), exports.ProvisionCompositionJsonValue);
exports.ProvisionCompositionKey = zod_1.z
    .string()
    .regex(/^[a-z][a-z0-9_.-]*$/)
    .max(160);
exports.ProvisionCompositionSchemaVersion = zod_1.z.literal(exports.PROVISION_COMPOSITION_SCHEMA_VERSION);
// Tills only. RL-449 added `link` to TerminalKind; Link is not provisioned
// through compositions (its packaging is RL-454), so the composition
// vocabulary stays exactly what it was.
exports.ProvisionCompositionApplicationType = terminal_js_1.TillKind;
exports.ProvisionEquipmentRoleRequirement = zod_1.z.enum(['required', 'optional', 'conditional']);
exports.ProvisionCompositionCondition = zod_1.z
    .object({
    fact: zod_1.z.enum([
        'application_type',
        'platform',
        'architecture',
        'country_pack',
        'core_version',
        'feature_capability',
    ]),
    operator: zod_1.z.enum(['equals', 'not_equals', 'in', 'not_in', 'satisfies']),
    value: exports.ProvisionCompositionJsonValue,
})
    .strict();
exports.ProvisionEquipmentRole = zod_1.z
    .object({
    roleKey: exports.ProvisionCompositionKey,
    displayName: zod_1.z.string().trim().min(1).max(160),
    requirement: exports.ProvisionEquipmentRoleRequirement,
    requirementReason: zod_1.z.string().trim().min(1).max(500).nullable(),
    conditions: zod_1.z.array(exports.ProvisionCompositionCondition).max(32),
    allowedClassKeys: zod_1.z.array(exports.ProvisionCompositionKey).min(1).max(64),
    minimumCardinality: zod_1.z.number().int().min(0).max(32),
    maximumCardinality: zod_1.z.number().int().min(1).max(32),
    scope: zod_1.z.enum(['endpoint', 'application']),
    sharing: zod_1.z.enum(['exclusive', 'same_host_when_supported']),
})
    .strict()
    .superRefine((role, context) => {
    if (role.minimumCardinality > role.maximumCardinality) {
        context.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['minimumCardinality'],
            message: 'Minimum cardinality cannot exceed maximum cardinality',
        });
    }
    if (role.requirement === 'conditional' && role.conditions.length === 0) {
        context.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['conditions'],
            message: 'Conditional roles require at least one applicability condition',
        });
    }
});
exports.ProvisionCompositionProfileVersion = zod_1.z
    .object({
    schemaVersion: exports.ProvisionCompositionSchemaVersion,
    profileId: zod_1.z.string().min(1).max(160),
    profileVersionId: zod_1.z.string().min(1).max(160),
    version: zod_1.z.number().int().positive(),
    applicationType: exports.ProvisionCompositionApplicationType,
    state: zod_1.z.enum(['draft', 'published', 'archived']),
    applicability: zod_1.z.array(exports.ProvisionCompositionCondition).max(64),
    equipmentRoles: zod_1.z.array(exports.ProvisionEquipmentRole).max(128),
    requiredComponentCapabilities: zod_1.z.array(exports.ProvisionCompositionKey).max(128),
    optionalComponentCapabilities: zod_1.z.array(exports.ProvisionCompositionKey).max(128),
    publishedAt: zod_1.z.string().datetime().nullable(),
    publisherId: zod_1.z.string().min(1).max(160),
})
    .strict();
exports.ProvisionConnectionField = zod_1.z
    .object({
    key: exports.ProvisionCompositionKey,
    label: zod_1.z.string().trim().min(1).max(160),
    valueType: zod_1.z.enum(['string', 'integer', 'boolean', 'enum', 'secret_reference']),
    required: zod_1.z.boolean(),
    defaultValue: exports.ProvisionCompositionJsonValue.optional(),
    allowedValues: zod_1.z.array(exports.ProvisionCompositionJsonValue).max(128).optional(),
    minimum: zod_1.z.number().finite().optional(),
    maximum: zod_1.z.number().finite().optional(),
})
    .strict();
exports.ProvisionEquipmentConnectionProfile = zod_1.z
    .object({
    connectionProfileId: zod_1.z.string().min(1).max(160),
    key: exports.ProvisionCompositionKey,
    connectionType: exports.ProvisionCompositionKey,
    compatiblePortKinds: zod_1.z.array(exports.ProvisionCompositionKey).min(1).max(32),
    sharing: zod_1.z.enum(['exclusive', 'shared']),
    requiredPortCapabilities: zod_1.z.array(exports.ProvisionCompositionKey).max(64),
    providedEquipmentCapabilities: zod_1.z.array(exports.ProvisionCompositionKey).max(64),
    compatibleComponentCapabilities: zod_1.z.array(exports.ProvisionCompositionKey).max(64),
    fields: zod_1.z.array(exports.ProvisionConnectionField).max(64),
    defaults: exports.ProvisionCompositionJsonObject,
})
    .strict();
exports.ProvisionEquipmentDiscoveryMatch = zod_1.z
    .object({
    usbVendorId: zod_1.z
        .string()
        .regex(/^[A-Fa-f0-9]{4}$/)
        .optional(),
    usbProductId: zod_1.z
        .string()
        .regex(/^[A-Fa-f0-9]{4}$/)
        .optional(),
    serviceType: zod_1.z.string().trim().min(1).max(160).optional(),
    vendorIdentifier: zod_1.z.string().trim().min(1).max(255).optional(),
})
    .strict();
exports.ProvisionEquipmentComponentCompatibility = zod_1.z
    .object({
    componentKey: exports.ProvisionCompositionKey.optional(),
    capability: exports.ProvisionCompositionKey.optional(),
    versionRange: zod_1.z
        .string()
        .trim()
        .min(1)
        .max(160)
        .refine(isProvisionVersionRangeValid, 'Invalid semantic version range'),
})
    .strict()
    .superRefine((compatibility, context) => {
    if ((compatibility.componentKey === undefined) === (compatibility.capability === undefined)) {
        context.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            message: 'Equipment compatibility must select exactly one component or capability',
        });
    }
});
exports.ProvisionEquipmentModelRevision = zod_1.z
    .object({
    schemaVersion: exports.ProvisionCompositionSchemaVersion,
    equipmentModelId: zod_1.z.string().min(1).max(160),
    equipmentModelRevisionId: zod_1.z.string().min(1).max(160),
    revision: zod_1.z.number().int().positive(),
    manufacturer: zod_1.z.string().trim().min(1).max(160),
    model: zod_1.z.string().trim().min(1).max(160),
    aliases: zod_1.z.array(zod_1.z.string().trim().min(1).max(160)).max(64),
    vendorIdentifiers: exports.ProvisionCompositionJsonObject,
    classKeys: zod_1.z.array(exports.ProvisionCompositionKey).min(1).max(32),
    capabilities: zod_1.z.array(exports.ProvisionCompositionKey).max(128),
    supportedRoles: zod_1.z.array(exports.ProvisionCompositionKey).max(128),
    connectionProfiles: zod_1.z.array(exports.ProvisionEquipmentConnectionProfile).min(1).max(32),
    discoveryMatches: zod_1.z.array(exports.ProvisionEquipmentDiscoveryMatch).max(64),
    compatibleComponents: zod_1.z.array(exports.ProvisionEquipmentComponentCompatibility).max(128),
    configurationSchema: exports.ProvisionCompositionJsonObject,
    configurationDefaults: exports.ProvisionCompositionJsonObject,
    applicableApplicationTypes: zod_1.z.array(exports.ProvisionCompositionApplicationType).max(3),
    applicablePlatforms: zod_1.z.array(exports.ProvisionCompositionKey).max(32),
    certification: zod_1.z.enum(['reform_supported', 'tenant_declared', 'unverified']),
    catalogueScope: zod_1.z.enum(['reform_global', 'tenant']),
    state: zod_1.z.enum(['draft', 'published', 'deprecated', 'archived']),
    supportNotes: zod_1.z.string().trim().max(2000).nullable(),
    sourceReference: zod_1.z.string().trim().max(500).nullable(),
})
    .strict();
exports.ProvisionComponentType = zod_1.z.enum([
    'core',
    'ui',
    'adapter',
    'driver',
    'sidecar',
    'fiscal_module',
    'payment_integration',
    'service',
    'other_registered',
]);
exports.ProvisionSoftwareComponentIdentity = zod_1.z
    .object({
    componentId: zod_1.z.string().min(1).max(160),
    key: exports.ProvisionCompositionKey,
    displayName: zod_1.z.string().trim().min(1).max(160),
    publisherId: zod_1.z.string().min(1).max(160),
    componentType: exports.ProvisionComponentType,
    catalogueScope: zod_1.z.enum(['reform_global', 'tenant']),
    description: zod_1.z.string().trim().max(1000).nullable(),
    archivedAt: zod_1.z.string().datetime().nullable(),
})
    .strict();
exports.ProvisionComponentDependency = zod_1.z
    .object({
    componentId: zod_1.z.string().min(1).max(160).optional(),
    capability: exports.ProvisionCompositionKey.optional(),
    versionRange: zod_1.z
        .string()
        .trim()
        .min(1)
        .max(160)
        .refine(isProvisionVersionRangeValid, 'Invalid semantic version range'),
    optional: zod_1.z.boolean(),
})
    .strict()
    .superRefine((dependency, context) => {
    if ((dependency.componentId === undefined) === (dependency.capability === undefined)) {
        context.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            message: 'A dependency must select exactly one component or capability',
        });
    }
});
exports.ProvisionComponentArtifactReference = zod_1.z
    .object({
    artifactId: zod_1.z.string().min(1).max(160),
    platform: exports.ProvisionCompositionKey,
    architecture: exports.ProvisionCompositionKey,
    objectKey: zod_1.z.string().min(1).max(1024),
    sizeBytes: zod_1.z.string().regex(/^\d+$/),
    sha256: zod_1.z.string().regex(/^[a-f0-9]{64}$/),
    signature: zod_1.z.string().min(16).max(4096),
    signingKeyId: zod_1.z.string().min(1).max(160),
    status: zod_1.z.enum(['published', 'revoked']),
})
    .strict();
/**
 * Executable peripheral packages use a deliberately narrower contract than
 * the general component catalogue. A valid document grants local executable
 * authority, so every field is closed and command-line arguments, shell
 * fragments, absolute paths, environment variables and inline secrets are
 * intentionally not representable.
 */
exports.ProvisionPeripheralPackageManifestVersion = zod_1.z.literal(1);
exports.ProvisionPeripheralLogicalRole = zod_1.z.enum([
    'receipt_printer',
    'barcode_scanner',
    'payment_terminal',
    'fiscal_service',
    'security_module',
    'scale',
    'cash_drawer',
    'customer_display',
]);
exports.ProvisionPeripheralRpcInterface = zod_1.z
    .object({
    logicalRole: exports.ProvisionPeripheralLogicalRole,
    capability: exports.ProvisionCompositionKey,
    versionRange: zod_1.z
        .string()
        .trim()
        .min(1)
        .max(160)
        .refine(isProvisionVersionRangeValid, 'Invalid RPC capability version range'),
})
    .strict();
exports.ProvisionPeripheralRpcContract = zod_1.z
    .object({
    protocol: zod_1.z.literal('reform_peripheral_rpc'),
    protocolVersionRange: zod_1.z
        .string()
        .trim()
        .min(1)
        .max(160)
        .refine(isProvisionVersionRangeValid, 'Invalid RPC protocol version range'),
    transport: zod_1.z.enum(['windows_named_pipe', 'unix_domain_socket', 'android_binder']),
    interfaces: zod_1.z.array(exports.ProvisionPeripheralRpcInterface).min(1).max(32),
    connectTimeoutMs: zod_1.z.number().int().min(100).max(120_000),
    operationTimeoutMs: zod_1.z.number().int().min(100).max(300_000),
    maximumMessageBytes: zod_1.z.number().int().min(1024).max(16_777_216),
})
    .strict()
    .superRefine((rpc, context) => {
    const interfaces = new Set();
    rpc.interfaces.forEach((entry, index) => {
        const identity = `${entry.logicalRole}:${entry.capability}`;
        if (interfaces.has(identity)) {
            context.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['interfaces', index],
                message: 'RPC logical role and capability pairs must be unique',
            });
        }
        interfaces.add(identity);
    });
});
exports.ProvisionPeripheralSecretSlot = zod_1.z
    .object({
    key: exports.ProvisionCompositionKey,
    purpose: zod_1.z.string().trim().min(1).max(300),
    required: zod_1.z.boolean(),
    acceptedProviders: zod_1.z
        .array(zod_1.z.enum(['os_keychain', 'terminal_vault', 'hardware_keystore']))
        .min(1)
        .max(3),
})
    .strict();
exports.ProvisionPeripheralConfigurationContract = zod_1.z
    .object({
    schemaVersion: zod_1.z.literal(1),
    allowUnknownFields: zod_1.z.literal(false),
    fields: zod_1.z.array(exports.ProvisionConnectionField).max(128),
    defaults: exports.ProvisionCompositionJsonObject,
    secretSlots: zod_1.z.array(exports.ProvisionPeripheralSecretSlot).max(32),
})
    .strict()
    .superRefine((configuration, context) => {
    const keys = new Set();
    configuration.fields.forEach((field, index) => {
        if (keys.has(field.key)) {
            context.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['fields', index, 'key'],
                message: 'Configuration field keys must be unique',
            });
        }
        keys.add(field.key);
        if (field.valueType === 'secret_reference') {
            context.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['fields', index, 'valueType'],
                message: 'Secrets must be declared as secret slots, not ordinary configuration fields',
            });
        }
    });
    configuration.secretSlots.forEach((slot, index) => {
        if (keys.has(slot.key)) {
            context.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['secretSlots', index, 'key'],
                message: 'Configuration and secret-slot keys must be unique',
            });
        }
        keys.add(slot.key);
    });
    Object.keys(configuration.defaults).forEach((key) => {
        if (!configuration.fields.some((field) => field.key === key)) {
            context.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['defaults', key],
                message: 'Defaults may only target declared non-secret configuration fields',
            });
        }
    });
});
exports.ProvisionPeripheralLifecycleContract = zod_1.z
    .object({
    activation: zod_1.z.enum(['live', 'when_idle', 'restart_required']),
    restartScope: zod_1.z.enum(['component', 'peripheral_host', 'terminal_application']),
    startupTimeoutSeconds: zod_1.z.number().int().min(1).max(600),
    shutdownTimeoutSeconds: zod_1.z.number().int().min(1).max(120),
    healthStabilizationSeconds: zod_1.z.number().int().min(0).max(600),
    rollbackOn: zod_1.z
        .array(zod_1.z.enum(['install_failure', 'start_failure', 'health_failure', 'rpc_incompatible']))
        .min(1)
        .max(4),
    retainLastKnownGoodVersions: zod_1.z.number().int().min(1).max(5),
})
    .strict();
exports.ProvisionPeripheralHealthContract = zod_1.z
    .object({
    protocol: zod_1.z.literal('reform_peripheral_health_v1'),
    startupProbe: zod_1.z.enum(['rpc_health', 'rpc_ready']),
    periodicProbeSeconds: zod_1.z.number().int().min(5).max(3600),
    probeTimeoutMs: zod_1.z.number().int().min(100).max(60_000),
    healthyStates: zod_1.z
        .array(zod_1.z.enum(['ready', 'operational']))
        .min(1)
        .max(2),
    requiresPeripheralEvidence: zod_1.z.boolean(),
})
    .strict();
exports.ProvisionPeripheralRuntimeContract = zod_1.z
    .object({
    kind: zod_1.z.enum(['native_process', 'external_service', 'android_service']),
    executableRelativePath: zod_1.z
        .string()
        .regex(/^[A-Za-z0-9][A-Za-z0-9._/-]{0,254}$/)
        .refine((value) => !value.startsWith('/') &&
        !value.includes('\\') &&
        !value.split('/').some((segment) => segment === '..' || segment.length === 0), 'Executable must be a normalized package-relative path')
        .nullable(),
    serviceId: exports.ProvisionCompositionKey,
    runAs: zod_1.z.enum(['restricted_component', 'local_service']),
    singleton: zod_1.z.boolean(),
    rpc: exports.ProvisionPeripheralRpcContract,
})
    .strict()
    .superRefine((runtime, context) => {
    if (runtime.kind === 'native_process' && runtime.executableRelativePath === null) {
        context.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['executableRelativePath'],
            message: 'Native process packages require a package-relative executable',
        });
    }
    if (runtime.kind !== 'native_process' && runtime.executableRelativePath !== null) {
        context.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['executableRelativePath'],
            message: 'Service packages cannot grant an additional executable path',
        });
    }
});
exports.ProvisionPeripheralReleaseIdentity = zod_1.z
    .object({
    publisherId: zod_1.z.string().min(1).max(160),
    releaseId: zod_1.z.string().min(1).max(160),
    releaseChannel: zod_1.z.enum(['stable', 'pilot', 'development']),
    manifestSha256: zod_1.z.string().regex(/^[a-f0-9]{64}$/),
    trustPolicyId: exports.ProvisionCompositionKey,
    acceptedSigningKeyIds: zod_1.z.array(zod_1.z.string().min(1).max(160)).min(1).max(16),
})
    .strict();
exports.ProvisionPeripheralPackageTarget = zod_1.z
    .object({
    applicationTypes: zod_1.z.array(exports.ProvisionCompositionApplicationType).min(1).max(3),
    platforms: zod_1.z
        .array(zod_1.z.enum(['windows', 'linux', 'android']))
        .min(1)
        .max(3),
    architectures: zod_1.z
        .array(zod_1.z.enum(['x64', 'arm64', 'armv7']))
        .min(1)
        .max(3),
    osVersionRanges: zod_1.z
        .array(zod_1.z
        .string()
        .trim()
        .min(1)
        .max(160)
        .refine(isProvisionVersionRangeValid, 'Invalid target OS version range'))
        .max(16),
    containingRuntimeVersionRange: zod_1.z
        .string()
        .trim()
        .min(1)
        .max(160)
        .refine(isProvisionVersionRangeValid, 'Invalid containing runtime version range'),
})
    .strict();
exports.ProvisionPeripheralPackageManifest = zod_1.z
    .object({
    manifestVersion: exports.ProvisionPeripheralPackageManifestVersion,
    manifestKind: zod_1.z.literal('deployable_peripheral_component'),
    packageId: exports.ProvisionCompositionKey,
    componentId: zod_1.z.string().min(1).max(160),
    componentVersionId: zod_1.z.string().min(1).max(160),
    componentType: exports.ProvisionComponentType,
    semanticVersion: zod_1.z
        .string()
        .trim()
        .min(1)
        .max(80)
        .refine(isProvisionSemanticVersion, 'Invalid component semantic version'),
    release: exports.ProvisionPeripheralReleaseIdentity,
    target: exports.ProvisionPeripheralPackageTarget,
    runtime: exports.ProvisionPeripheralRuntimeContract,
    providesCapabilities: zod_1.z.array(exports.ProvisionCompositionKey).min(1).max(128),
    capabilityVersions: zod_1.z.record(exports.ProvisionCompositionKey, zod_1.z.string().trim().min(1).max(80)),
    dependencies: zod_1.z.array(exports.ProvisionComponentDependency).max(128),
    conflicts: zod_1.z.array(exports.ProvisionComponentDependency).max(128),
    upgradeFromRanges: zod_1.z
        .array(zod_1.z.string().trim().min(1).max(160).refine(isProvisionVersionRangeValid))
        .max(64),
    downgradeToRanges: zod_1.z
        .array(zod_1.z.string().trim().min(1).max(160).refine(isProvisionVersionRangeValid))
        .max(64),
    configuration: exports.ProvisionPeripheralConfigurationContract,
    lifecycle: exports.ProvisionPeripheralLifecycleContract,
    health: exports.ProvisionPeripheralHealthContract,
    artifacts: zod_1.z.array(exports.ProvisionComponentArtifactReference).min(1).max(16),
})
    .strict()
    .superRefine((manifest, context) => {
    if (manifest.componentType === 'core' || manifest.componentType === 'ui') {
        context.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['componentType'],
            message: 'Core and UI releases cannot receive peripheral package authority',
        });
    }
    if (manifest.runtime.kind === 'android_service' &&
        !manifest.target.platforms.includes('android')) {
        context.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['target', 'platforms'],
            message: 'Android service packages must target Android',
        });
    }
    if (manifest.runtime.kind !== 'android_service' &&
        manifest.target.platforms.includes('android')) {
        context.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['runtime', 'kind'],
            message: 'Android targets require the Android service runtime boundary',
        });
    }
    if (manifest.runtime.rpc.transport === 'android_binder') {
        if (manifest.runtime.kind !== 'android_service') {
            context.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['runtime', 'rpc', 'transport'],
                message: 'Binder transport is only valid for Android services',
            });
        }
    }
    else if (manifest.runtime.kind === 'android_service') {
        context.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['runtime', 'rpc', 'transport'],
            message: 'Android services must use Binder transport',
        });
    }
    const signingKeys = new Set(manifest.release.acceptedSigningKeyIds);
    manifest.artifacts.forEach((artifact, index) => {
        if (artifact.status !== 'published') {
            context.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['artifacts', index, 'status'],
                message: 'A deployable manifest cannot authorize a revoked artifact',
            });
        }
        if (!manifest.target.platforms.includes(artifact.platform)) {
            context.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['artifacts', index, 'platform'],
                message: 'Artifact platform is outside the package target',
            });
        }
        if (!manifest.target.architectures.includes(artifact.architecture)) {
            context.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['artifacts', index, 'architecture'],
                message: 'Artifact architecture is outside the package target',
            });
        }
        if (!signingKeys.has(artifact.signingKeyId)) {
            context.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['artifacts', index, 'signingKeyId'],
                message: 'Artifact signing key is not authorized by the release identity',
            });
        }
    });
    manifest.runtime.rpc.interfaces.forEach((rpcInterface, index) => {
        if (!manifest.providesCapabilities.includes(rpcInterface.capability)) {
            context.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['runtime', 'rpc', 'interfaces', index, 'capability'],
                message: 'RPC interfaces must expose a declared package capability',
            });
        }
    });
});
/**
 * RL-957 S4 (owner Q2): whether a component version is a certified build or a
 * test build. Separate from release channel and trust. A manifest from before
 * the field reads as a test build.
 */
exports.ProvisionComponentCertification = zod_1.z.enum(['certified', 'test']);
exports.ProvisionComponentVersionManifest = zod_1.z
    .object({
    schemaVersion: exports.ProvisionCompositionSchemaVersion,
    componentId: zod_1.z.string().min(1).max(160),
    componentVersionId: zod_1.z.string().min(1).max(160),
    componentType: exports.ProvisionComponentType,
    semanticVersion: zod_1.z
        .string()
        .trim()
        .min(1)
        .max(80)
        .refine(isProvisionSemanticVersion, 'Invalid semantic version'),
    state: zod_1.z.enum(['draft', 'published', 'deprecated', 'revoked', 'archived']),
    applicationTypes: zod_1.z.array(exports.ProvisionCompositionApplicationType).max(3),
    platforms: zod_1.z.array(exports.ProvisionCompositionKey).max(32),
    architectures: zod_1.z.array(exports.ProvisionCompositionKey).max(32),
    osVersionRanges: zod_1.z
        .array(zod_1.z
        .string()
        .trim()
        .min(1)
        .max(160)
        .refine(isProvisionVersionRangeValid, 'Invalid OS semantic version range'))
        .max(32),
    providesCapabilities: zod_1.z.array(exports.ProvisionCompositionKey).max(128),
    capabilityVersions: zod_1.z.record(exports.ProvisionCompositionKey, zod_1.z.string().trim().min(1).max(80)),
    dependencies: zod_1.z.array(exports.ProvisionComponentDependency).max(128),
    conflicts: zod_1.z.array(exports.ProvisionComponentDependency).max(128),
    compatibleEquipmentModelRevisionIds: zod_1.z.array(zod_1.z.string().min(1).max(160)).max(256),
    compatibleClassKeys: zod_1.z.array(exports.ProvisionCompositionKey).max(128),
    compatibleConnectionTypes: zod_1.z.array(exports.ProvisionCompositionKey).max(64),
    compatibleRoles: zod_1.z.array(exports.ProvisionCompositionKey).max(128),
    upgradeFromRanges: zod_1.z
        .array(zod_1.z.string().trim().min(1).max(160).refine(isProvisionVersionRangeValid))
        .max(64),
    downgradeToRanges: zod_1.z
        .array(zod_1.z.string().trim().min(1).max(160).refine(isProvisionVersionRangeValid))
        .max(64),
    deliveryMode: zod_1.z.enum([
        'bundled',
        'provision_artifact',
        'os_package',
        'external_service',
        'manual',
    ]),
    containingComponentVersionId: zod_1.z.string().min(1).max(160).nullable(),
    artifacts: zod_1.z.array(exports.ProvisionComponentArtifactReference).max(128),
    packageManifest: exports.ProvisionPeripheralPackageManifest.nullable().default(null),
    activationPolicy: zod_1.z.enum(['live', 'when_idle', 'restart_required']),
    configurationSchema: exports.ProvisionCompositionJsonObject,
    configurationDefaults: exports.ProvisionCompositionJsonObject,
    trusted: zod_1.z.boolean(),
    tenantApproved: zod_1.z.boolean(),
    certification: exports.ProvisionComponentCertification.default('test'),
    rowVersion: zod_1.z.number().int().positive().default(1),
    approvalStatus: zod_1.z.enum(['approved', 'revoked']).nullable(),
    releaseNotes: zod_1.z.string().max(20_000).nullable(),
    manifestChecksum: zod_1.z.string().regex(/^[a-f0-9]{64}$/),
    publishedAt: zod_1.z.string().datetime().nullable(),
})
    .strict();
exports.ProvisionEndpointPortKind = zod_1.z.enum([
    'usb',
    'serial',
    'network',
    'local_socket',
    'service',
    'keyboard_wedge',
    'other_registered',
]);
exports.ProvisionEndpointPortObservation = zod_1.z
    .object({
    stablePortKey: zod_1.z.string().min(1).max(255),
    kind: exports.ProvisionEndpointPortKind,
    label: zod_1.z.string().trim().min(1).max(160),
    currentAddress: zod_1.z.string().trim().min(1).max(500).nullable(),
    aliases: zod_1.z.array(zod_1.z.string().trim().min(1).max(500)).max(64),
    identityStrength: zod_1.z.enum(['strong', 'topology', 'manual']),
    source: zod_1.z.enum(['terminal', 'manual', 'imported']),
    shareMode: zod_1.z.enum(['exclusive', 'shared']),
    capacity: zod_1.z.number().int().positive().nullable(),
    capabilities: zod_1.z.array(exports.ProvisionCompositionKey).max(128),
    discoveryEvidence: exports.ProvisionCompositionJsonObject,
    present: zod_1.z.boolean(),
    health: zod_1.z.enum(['unknown', 'healthy', 'degraded', 'failed']),
    observedAt: zod_1.z.string().datetime(),
})
    .strict();
exports.ProvisionObservationStatus = zod_1.z.enum(['present', 'absent', 'stale', 'unknown']);
exports.ProvisionObservedComponentPackageEvidence = zod_1.z
    .object({
    packageId: exports.ProvisionCompositionKey,
    manifestSha256: zod_1.z.string().regex(/^[a-f0-9]{64}$/),
    verification: zod_1.z.enum(['verified', 'rejected', 'unknown']),
    lifecycleState: zod_1.z.enum([
        'staged',
        'installed',
        'starting',
        'active',
        'failed',
        'rolling_back',
        'rolled_back',
    ]),
    rpcProtocolVersion: zod_1.z.string().trim().min(1).max(80).nullable(),
    activeConfigurationChecksum: zod_1.z
        .string()
        .regex(/^[a-f0-9]{64}$/)
        .nullable(),
    lastKnownGoodVersionId: zod_1.z.string().min(1).max(160).nullable(),
    errorCode: zod_1.z
        .string()
        .regex(/^[A-Z0-9_.-]+$/)
        .max(120)
        .nullable(),
    observedAt: zod_1.z.string().datetime(),
})
    .strict();
exports.ProvisionObservedComponent = zod_1.z
    .object({
    componentId: zod_1.z.string().min(1).max(160),
    componentType: exports.ProvisionComponentType,
    semanticVersion: zod_1.z.string().trim().min(1).max(80),
    componentVersionId: zod_1.z.string().min(1).max(160).nullable(),
    containingComponentId: zod_1.z.string().min(1).max(160).nullable(),
    artifactId: zod_1.z.string().min(1).max(160).nullable(),
    checksum: zod_1.z
        .string()
        .regex(/^[a-f0-9]{64}$/)
        .nullable(),
    capabilities: zod_1.z.array(exports.ProvisionCompositionKey).max(128),
    capabilityVersions: zod_1.z
        .record(exports.ProvisionCompositionKey, zod_1.z.string().trim().min(1).max(80))
        .default({}),
    health: zod_1.z.enum(['unknown', 'healthy', 'degraded', 'failed']),
    packageEvidence: exports.ProvisionObservedComponentPackageEvidence.nullable().optional(),
})
    .strict();
exports.ProvisionEndpointInventorySnapshot = zod_1.z
    .object({
    schemaVersion: exports.ProvisionCompositionSchemaVersion,
    clientEventId: zod_1.z.string().uuid(),
    endpointId: zod_1.z.string().min(1).max(160),
    applicationInstanceId: zod_1.z.string().min(1).max(160),
    applicationType: exports.ProvisionCompositionApplicationType,
    generation: zod_1.z.number().int().nonnegative(),
    complete: zod_1.z.boolean(),
    platform: exports.ProvisionCompositionKey,
    architecture: exports.ProvisionCompositionKey,
    osVersion: zod_1.z.string().trim().min(1).max(160),
    supportedSchemaVersions: zod_1.z.array(zod_1.z.number().int().positive()).min(1).max(32),
    ports: zod_1.z.array(exports.ProvisionEndpointPortObservation).max(512),
    components: zod_1.z.array(exports.ProvisionObservedComponent).max(512),
    reportedAt: zod_1.z.string().datetime(),
})
    .strict()
    .superRefine((snapshot, context) => {
    const stableKeys = new Set();
    snapshot.ports.forEach((port, index) => {
        if (stableKeys.has(port.stablePortKey)) {
            context.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['ports', index, 'stablePortKey'],
                message: 'Stable port keys must be unique within one snapshot',
            });
        }
        stableKeys.add(port.stablePortKey);
    });
    const componentIds = new Set();
    snapshot.components.forEach((component, index) => {
        if (componentIds.has(component.componentId)) {
            context.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['components', index, 'componentId'],
                message: 'Observed component identities must be unique within one snapshot',
            });
        }
        componentIds.add(component.componentId);
    });
});
const canonicalHardwareBindingShape = {
    deviceId: zod_1.z.string().min(1).max(160),
    classKey: exports.ProvisionCompositionKey,
    adapterId: zod_1.z
        .string()
        .regex(/^[a-z][a-z0-9_.-]*$/)
        .max(160),
    supportedVersion: zod_1.z.string().trim().max(80).nullable().optional(),
    connectionType: exports.ProvisionCompositionKey,
    connectionParameters: exports.ProvisionCompositionJsonObject,
    secretReference: zod_1.z.string().trim().max(255).nullable().optional(),
    required: zod_1.z.boolean(),
    config: exports.ProvisionCompositionJsonObject,
    equipmentModelRevisionId: zod_1.z.string().min(1).max(160).optional(),
    endpointPortId: zod_1.z.string().min(1).max(160).optional(),
    adapterComponentVersionId: zod_1.z.string().min(1).max(160).optional(),
};
/**
 * Runtime-compatible hardware binding. Unknown top-level JSON fields are
 * intentionally retained for Advanced/manual round-trip.
 */
exports.ProvisionCanonicalHardwareBinding = zod_1.z
    .object(canonicalHardwareBindingShape)
    .catchall(exports.ProvisionCompositionJsonValue);
/** Managed authoring rejects stray structure and requires catalogue identity. */
exports.ProvisionManagedHardwareBinding = zod_1.z
    .object({
    ...canonicalHardwareBindingShape,
    equipmentModelRevisionId: zod_1.z.string().min(1).max(160),
    adapterComponentVersionId: zod_1.z.string().min(1).max(160),
})
    .strict();
exports.ProvisionCanonicalHardwareProjection = zod_1.z.record(exports.ProvisionCompositionKey, exports.ProvisionCanonicalHardwareBinding);
const canonicalSoftwareComponentShape = {
    componentId: zod_1.z.string().min(1).max(160),
    componentType: exports.ProvisionComponentType,
    version: zod_1.z.string().trim().min(1).max(80),
    componentVersionId: zod_1.z.string().min(1).max(160).optional(),
    artifactId: zod_1.z.string().min(1).max(160).nullable().optional(),
    configuration: exports.ProvisionCompositionJsonObject,
};
exports.ProvisionCanonicalSoftwareComponent = zod_1.z
    .object(canonicalSoftwareComponentShape)
    .catchall(exports.ProvisionCompositionJsonValue);
exports.ProvisionManagedSoftwareComponent = zod_1.z
    .object({
    ...canonicalSoftwareComponentShape,
    componentVersionId: zod_1.z.string().min(1).max(160),
})
    .strict();
exports.ProvisionCanonicalSoftwareProjection = zod_1.z
    .object({
    components: zod_1.z.record(exports.ProvisionCompositionKey, exports.ProvisionCanonicalSoftwareComponent),
})
    .catchall(exports.ProvisionCompositionJsonValue);
exports.ProvisionCanonicalComposition = zod_1.z
    .object({
    schemaVersion: exports.ProvisionCompositionSchemaVersion,
    applicationInstanceId: zod_1.z.string().min(1).max(160),
    applicationType: exports.ProvisionCompositionApplicationType,
    profileVersionId: zod_1.z.string().min(1).max(160).nullable(),
    hardware: exports.ProvisionCanonicalHardwareProjection,
    software: exports.ProvisionCanonicalSoftwareProjection,
    extensions: exports.ProvisionCompositionJsonObject.optional(),
})
    .catchall(exports.ProvisionCompositionJsonValue);
exports.ProvisionCompositionReason = zod_1.z
    .object({
    code: zod_1.z
        .string()
        .regex(/^[A-Z0-9_.-]+$/)
        .max(120),
    message: zod_1.z.string().trim().min(1).max(500),
    ruleId: zod_1.z.string().min(1).max(160).nullable(),
    path: zod_1.z.string().trim().min(1).max(500).nullable(),
})
    .strict();
exports.ProvisionCompositionCandidateKind = zod_1.z.enum([
    'profile',
    'equipment_model',
    'asset',
    'endpoint_port',
    'connection_method',
    'component',
    'component_version',
]);
exports.ProvisionCompositionCandidate = zod_1.z
    .object({
    schemaVersion: exports.ProvisionCompositionSchemaVersion,
    kind: exports.ProvisionCompositionCandidateKind,
    candidateId: zod_1.z.string().min(1).max(160),
    displayName: zod_1.z.string().trim().min(1).max(255),
    eligible: zod_1.z.boolean(),
    reasons: zod_1.z.array(exports.ProvisionCompositionReason).max(128),
    lifecycle: zod_1.z.string().trim().min(1).max(80),
    trusted: zod_1.z.boolean().nullable(),
    metadata: exports.ProvisionCompositionJsonObject,
})
    .strict();
exports.ProvisionCompatibilitySelection = zod_1.z
    .object({
    roleKey: exports.ProvisionCompositionKey.nullable(),
    equipmentModelRevisionId: zod_1.z.string().min(1).max(160).nullable(),
    assetId: zod_1.z.string().min(1).max(160).nullable(),
    endpointPortId: zod_1.z.string().min(1).max(160).nullable(),
    peripheralBindingId: zod_1.z.string().min(1).max(160).nullable().default(null),
    connectionProfileId: zod_1.z.string().min(1).max(160).nullable(),
    adapterComponentVersionId: zod_1.z.string().min(1).max(160).nullable(),
    componentVersionIds: zod_1.z.array(zod_1.z.string().min(1).max(160)).max(512),
    manualMode: zod_1.z.boolean(),
})
    .strict()
    .superRefine((selection, context) => {
    const seen = new Set();
    selection.componentVersionIds.forEach((componentVersionId, index) => {
        if (seen.has(componentVersionId)) {
            context.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['componentVersionIds', index],
                message: 'Selected component version ids must be unique',
            });
        }
        seen.add(componentVersionId);
    });
});
exports.ProvisionCompositionCandidateRequest = zod_1.z
    .object({
    schemaVersion: exports.ProvisionCompositionSchemaVersion,
    kind: exports.ProvisionCompositionCandidateKind.exclude(['profile']),
    selection: exports.ProvisionCompatibilitySelection,
    includeIncompatible: zod_1.z.boolean().default(false),
    currentCandidateId: zod_1.z.string().min(1).max(160).nullable().default(null),
})
    .strict();
exports.ProvisionCompositionCandidateResponse = zod_1.z
    .object({
    schemaVersion: exports.ProvisionCompositionSchemaVersion,
    kind: exports.ProvisionCompositionCandidateKind.exclude(['profile']),
    candidates: zod_1.z.array(exports.ProvisionCompositionCandidate).max(4096),
    autoSelectCandidateId: zod_1.z.string().min(1).max(160).nullable(),
})
    .strict();
exports.ProvisionRawFieldState = zod_1.z.discriminatedUnion('present', [
    zod_1.z.object({ present: zod_1.z.literal(false) }).strict(),
    zod_1.z.object({ present: zod_1.z.literal(true), value: exports.ProvisionCompositionJsonValue }).strict(),
]);
exports.ProvisionRawFieldDifference = zod_1.z
    .object({
    path: zod_1.z.string().startsWith('/').max(1000),
    kind: zod_1.z.enum(['added', 'removed', 'changed']),
    before: exports.ProvisionRawFieldState,
    after: exports.ProvisionRawFieldState,
})
    .strict();
exports.ProvisionPeripheralManualBinding = zod_1.z
    .object({
    bindingId: zod_1.z.string().min(1).max(160),
    applicationInstanceId: zod_1.z.string().min(1).max(160).nullable(),
    logicalRole: exports.ProvisionCompositionKey,
    required: zod_1.z.boolean(),
    config: exports.ProvisionCompositionJsonObject,
    connectionProfileId: zod_1.z.string().min(1).max(160).nullable(),
    endpointPortId: zod_1.z.string().min(1).max(160).nullable(),
    adapterComponentVersionId: zod_1.z.string().min(1).max(160).nullable(),
    catalogueMatchStatus: zod_1.z.enum([
        'custom_legacy',
        'proposed',
        'managed',
        'ambiguous',
        'unavailable',
    ]),
    catalogueMatchMetadata: exports.ProvisionCompositionJsonValue.nullable(),
})
    .strict();
exports.ProvisionPeripheralManualRecord = zod_1.z
    .object({
    schemaVersion: exports.ProvisionCompositionSchemaVersion,
    deviceId: zod_1.z.string().min(1).max(160),
    version: zod_1.z.number().int().positive(),
    endpointId: zod_1.z.string().min(1).max(160).nullable(),
    lifecycle: zod_1.z.enum(['available', 'assigned', 'maintenance', 'retired']),
    classKey: exports.ProvisionCompositionKey,
    vendor: zod_1.z.string().max(120).nullable(),
    model: zod_1.z.string().max(120).nullable(),
    serialAssetId: zod_1.z.string().max(160).nullable(),
    connectionType: exports.ProvisionCompositionKey,
    connectionParameters: exports.ProvisionCompositionJsonObject,
    adapterId: exports.ProvisionCompositionKey,
    supportedVersion: zod_1.z.string().max(80).nullable(),
    secretReference: zod_1.z.string().max(255).nullable(),
    equipmentModelRevisionId: zod_1.z.string().min(1).max(160).nullable(),
    catalogueMatchStatus: zod_1.z.enum([
        'custom_legacy',
        'proposed',
        'managed',
        'ambiguous',
        'unavailable',
    ]),
    catalogueMatchMetadata: exports.ProvisionCompositionJsonValue.nullable(),
    bindings: zod_1.z.array(exports.ProvisionPeripheralManualBinding).max(32),
})
    .strict();
exports.ProvisionLegacyMatchProposal = zod_1.z
    .object({
    schemaVersion: exports.ProvisionCompositionSchemaVersion,
    entityType: zod_1.z.enum(['device', 'binding']),
    entityId: zod_1.z.string().min(1).max(160),
    rawChecksum: zod_1.z.string().regex(/^[a-f0-9]{64}$/),
    result: zod_1.z.enum(['none', 'exact', 'ambiguous']),
    candidates: zod_1.z.array(exports.ProvisionCompositionCandidate).max(256),
    evidence: exports.ProvisionCompositionJsonObject,
    generatedAt: zod_1.z.string().datetime(),
})
    .strict();
exports.ProvisionCompositionFinding = zod_1.z
    .object({
    severity: zod_1.z.enum(['error', 'warning', 'information']),
    reason: exports.ProvisionCompositionReason,
    acknowledgeable: zod_1.z.boolean(),
    entityKind: zod_1.z.string().trim().min(1).max(80),
    entityId: zod_1.z.string().min(1).max(160).nullable(),
})
    .strict();
exports.ProvisionCompositionValidationRequest = zod_1.z
    .object({
    schemaVersion: exports.ProvisionCompositionSchemaVersion,
    composition: exports.ProvisionCanonicalComposition,
    manualMode: zod_1.z.boolean(),
    selections: zod_1.z.array(exports.ProvisionCompatibilitySelection).max(128),
    acknowledgedWarningCodes: zod_1.z
        .array(zod_1.z
        .string()
        .regex(/^[A-Z0-9_.-]+$/)
        .max(120))
        .max(128)
        .default([]),
})
    .strict();
exports.ProvisionCompositionValidation = zod_1.z
    .object({
    schemaVersion: exports.ProvisionCompositionSchemaVersion,
    valid: zod_1.z.boolean(),
    composition: exports.ProvisionCanonicalComposition,
    findings: zod_1.z.array(exports.ProvisionCompositionFinding).max(1024),
    strictestActivationPolicy: zod_1.z.enum(['live', 'when_idle', 'restart_required']),
    dependencyOrder: zod_1.z.array(zod_1.z.string().min(1).max(160)).max(512),
})
    .strict()
    .superRefine((validation, context) => {
    const hasError = validation.findings.some((finding) => finding.severity === 'error');
    if (validation.valid === hasError) {
        context.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['valid'],
            message: 'Validation is valid exactly when it contains no error findings',
        });
    }
});
exports.ProvisionCompositionAuthoringMode = zod_1.z.enum(['managed', 'manual']);
const acknowledgedWarningCodes = zod_1.z
    .array(zod_1.z
    .string()
    .regex(/^[A-Z0-9_.-]+$/)
    .max(120))
    .max(128)
    .default([]);
const provisionCompositionDraftContentShape = {
    schemaVersion: exports.ProvisionCompositionSchemaVersion,
    authoringMode: exports.ProvisionCompositionAuthoringMode,
    composition: exports.ProvisionCanonicalComposition,
    selections: zod_1.z.array(exports.ProvisionCompatibilitySelection).max(128),
    acknowledgedWarningCodes,
};
function validateProvisionCompositionDraftContent(draft, context) {
    const manualMode = draft.authoringMode === 'manual';
    draft.selections.forEach((selection, index) => {
        if (selection.manualMode !== manualMode) {
            context.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['selections', index, 'manualMode'],
                message: 'Selection mode must agree with the draft authoring mode',
            });
        }
    });
    if (manualMode)
        return;
    Object.entries(draft.composition.hardware).forEach(([roleKey, hardware]) => {
        const parsed = exports.ProvisionManagedHardwareBinding.safeParse(hardware);
        if (!parsed.success) {
            context.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['composition', 'hardware', roleKey],
                message: 'Managed hardware must use the strict catalogue-backed shape',
            });
        }
    });
    Object.entries(draft.composition.software.components).forEach(([roleKey, component]) => {
        const parsed = exports.ProvisionManagedSoftwareComponent.safeParse(component);
        if (!parsed.success) {
            context.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['composition', 'software', 'components', roleKey],
                message: 'Managed software must use the strict catalogue-backed shape',
            });
        }
    });
}
exports.ProvisionCompositionDraftContent = zod_1.z
    .object(provisionCompositionDraftContentShape)
    .strict()
    .superRefine(validateProvisionCompositionDraftContent);
exports.ProvisionCompositionDraftUpsertRequest = zod_1.z
    .object({
    ...provisionCompositionDraftContentShape,
    expectedVersion: zod_1.z.number().int().positive().nullable(),
})
    .strict()
    .superRefine(validateProvisionCompositionDraftContent);
exports.ProvisionCompositionDraft = zod_1.z
    .object({
    ...provisionCompositionDraftContentShape,
    draftId: zod_1.z.string().min(1).max(160),
    version: zod_1.z.number().int().positive(),
    createdAt: zod_1.z.string().datetime(),
    updatedAt: zod_1.z.string().datetime(),
    lastAppliedAt: zod_1.z.string().datetime().nullable(),
    lastAppliedChecksum: zod_1.z
        .string()
        .regex(/^[a-f0-9]{64}$/)
        .nullable(),
})
    .strict()
    .superRefine(validateProvisionCompositionDraftContent);
exports.ProvisionCompositionDraftPreviewRequest = zod_1.z
    .object({
    schemaVersion: exports.ProvisionCompositionSchemaVersion,
    expectedDraftVersion: zod_1.z.number().int().positive(),
})
    .strict();
exports.ProvisionCompositionHistoryEntry = zod_1.z
    .object({
    schemaVersion: exports.ProvisionCompositionSchemaVersion,
    historyId: zod_1.z.string().min(1).max(160),
    applicationInstanceId: zod_1.z.string().min(1).max(160),
    sequence: zod_1.z.number().int().positive(),
    profileVersionId: zod_1.z.string().min(1).max(160).nullable(),
    desiredStateRevisionId: zod_1.z.string().min(1).max(160).nullable(),
    composition: exports.ProvisionCanonicalComposition,
    checksum: zod_1.z.string().regex(/^[a-f0-9]{64}$/),
    reason: zod_1.z.string().trim().min(1).max(1000),
    createdAt: zod_1.z.string().datetime(),
    createdBy: zod_1.z.string().min(1).max(160).nullable(),
})
    .strict();
exports.ProvisionCompositionExpectedRecordKind = zod_1.z.enum([
    'profile_version',
    'equipment_model_revision',
    'asset',
    'peripheral_binding',
    'endpoint_port',
    'component_version',
    'component_binding',
]);
exports.ProvisionCompositionExpectedRecord = zod_1.z
    .object({
    kind: exports.ProvisionCompositionExpectedRecordKind,
    id: zod_1.z.string().min(1).max(160),
    version: zod_1.z.number().int().positive(),
})
    .strict();
exports.ProvisionCompositionPortClaimPreview = zod_1.z
    .object({
    roleKey: exports.ProvisionCompositionKey,
    endpointPortId: zod_1.z.string().min(1).max(160),
    peripheralBindingId: zod_1.z.string().min(1).max(160).nullable(),
    exclusive: zod_1.z.boolean(),
    units: zod_1.z.number().int().positive(),
    action: zod_1.z.enum(['acquire', 'retain', 'release']),
})
    .strict();
exports.ProvisionCompositionDraftPreview = zod_1.z
    .object({
    schemaVersion: exports.ProvisionCompositionSchemaVersion,
    draftId: zod_1.z.string().min(1).max(160),
    draftVersion: zod_1.z.number().int().positive(),
    materializedComposition: exports.ProvisionCanonicalComposition,
    validation: exports.ProvisionCompositionValidation,
    canonicalChecksum: zod_1.z.string().regex(/^[a-f0-9]{64}$/),
    previewChecksum: zod_1.z.string().regex(/^[a-f0-9]{64}$/),
    expectedCompositionRevision: zod_1.z.number().int().positive(),
    expectedProfileRevision: zod_1.z.number().int().positive(),
    expectedHistorySequence: zod_1.z.number().int().nonnegative(),
    expectedObservationGeneration: zod_1.z.number().int().nonnegative().nullable(),
    expectedRecords: zod_1.z.array(exports.ProvisionCompositionExpectedRecord).max(1024),
    desiredStateRevision: zod_1.z.number().int().nonnegative(),
    desiredStateChecksum: zod_1.z.string().regex(/^[a-f0-9]{64}$/),
    desiredStateChanges: zod_1.z.array(exports.ProvisionRawFieldDifference).max(4096),
    portClaims: zod_1.z.array(exports.ProvisionCompositionPortClaimPreview).max(256),
    affectedApplicationIds: zod_1.z.array(zod_1.z.string().min(1).max(160)).min(1).max(256),
    largeScopeConfirmationRequired: zod_1.z.boolean(),
    changed: zod_1.z.boolean(),
})
    .strict();
exports.ProvisionCompositionDraftApplyRequest = zod_1.z
    .object({
    schemaVersion: exports.ProvisionCompositionSchemaVersion,
    expectedDraftVersion: zod_1.z.number().int().positive(),
    expectedCompositionRevision: zod_1.z.number().int().positive(),
    expectedProfileRevision: zod_1.z.number().int().positive(),
    expectedHistorySequence: zod_1.z.number().int().nonnegative(),
    expectedObservationGeneration: zod_1.z.number().int().nonnegative().nullable(),
    expectedRecords: zod_1.z.array(exports.ProvisionCompositionExpectedRecord).max(1024),
    previewChecksum: zod_1.z.string().regex(/^[a-f0-9]{64}$/),
    reason: zod_1.z.string().trim().min(1).max(1000),
    confirmLargeScope: zod_1.z.boolean().default(false),
})
    .strict();
exports.ProvisionCompositionDraftApplyResult = zod_1.z
    .object({
    schemaVersion: exports.ProvisionCompositionSchemaVersion,
    changed: zod_1.z.boolean(),
    draftVersion: zod_1.z.number().int().positive(),
    compositionRevision: zod_1.z.number().int().positive(),
    canonicalChecksum: zod_1.z.string().regex(/^[a-f0-9]{64}$/),
    compositionHistoryId: zod_1.z.string().min(1).max(160).nullable(),
    compositionHistorySequence: zod_1.z.number().int().nonnegative(),
    desiredStateRevisionId: zod_1.z.string().min(1).max(160).nullable(),
    desiredStateRevision: zod_1.z.number().int().nonnegative(),
    desiredStateChecksum: zod_1.z.string().regex(/^[a-f0-9]{64}$/),
    portClaims: zod_1.z.array(exports.ProvisionCompositionPortClaimPreview).max(256),
})
    .strict();
exports.ProvisionCompositionDrift = zod_1.z
    .object({
    schemaVersion: exports.ProvisionCompositionSchemaVersion,
    applicationInstanceId: zod_1.z.string().min(1).max(160),
    status: zod_1.z.enum([
        'in_sync',
        'pending',
        'unknown',
        'unsupported',
        'stale',
        'mismatch',
        'partial',
        'failed',
    ]),
    entityKind: zod_1.z.enum([
        'composition_profile',
        'equipment_role',
        'asset',
        'endpoint_port',
        'component',
        'dependency',
        'trust',
        'health',
    ]),
    entityId: zod_1.z.string().min(1).max(160).nullable(),
    desired: exports.ProvisionCompositionJsonValue.optional(),
    observed: exports.ProvisionCompositionJsonValue.optional(),
    reason: exports.ProvisionCompositionReason,
})
    .strict();
/**
 * Closed, side-effect-free SemVer evaluator for Provision catalogue metadata.
 * Unsupported or partially parsed syntax is invalid and never matches.
 */
function satisfiesProvisionVersionRange(version, range) {
    const parsed = parseProvisionVersion(version);
    const parsedRange = parseProvisionVersionRange(range);
    if (parsed === null || parsedRange === null)
        return false;
    return parsedRange.some((alternative) => (parsed.prerelease.length === 0 ||
        alternative.prereleaseCores.has(provisionVersionCore(parsed))) &&
        alternative.comparators.every((comparator) => comparator(parsed)));
}
function isProvisionVersionRangeValid(range) {
    return parseProvisionVersionRange(range) !== null;
}
function isProvisionSemanticVersion(version) {
    return parseProvisionVersion(version) !== null;
}
function parseProvisionVersion(value) {
    const match = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-([0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*))?(?:\+[0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*)?$/.exec(value);
    if (match === null)
        return null;
    const prerelease = match[4]?.split('.') ?? [];
    if (prerelease.some((identifier) => /^\d+$/.test(identifier) && /^0\d+/.test(identifier))) {
        return null;
    }
    return {
        major: Number(match[1]),
        minor: Number(match[2]),
        patch: Number(match[3]),
        prerelease,
    };
}
function compareProvisionVersions(left, right) {
    const numeric = left.major - right.major || left.minor - right.minor || left.patch - right.patch;
    if (numeric !== 0)
        return numeric;
    if (left.prerelease.length === 0 || right.prerelease.length === 0) {
        return left.prerelease.length === right.prerelease.length
            ? 0
            : left.prerelease.length === 0
                ? 1
                : -1;
    }
    const length = Math.max(left.prerelease.length, right.prerelease.length);
    for (let index = 0; index < length; index += 1) {
        const leftIdentifier = left.prerelease[index];
        const rightIdentifier = right.prerelease[index];
        if (leftIdentifier === undefined || rightIdentifier === undefined) {
            return leftIdentifier === undefined ? -1 : 1;
        }
        if (leftIdentifier === rightIdentifier)
            continue;
        const leftNumeric = /^\d+$/.test(leftIdentifier);
        const rightNumeric = /^\d+$/.test(rightIdentifier);
        if (leftNumeric && rightNumeric)
            return Number(leftIdentifier) - Number(rightIdentifier);
        if (leftNumeric !== rightNumeric)
            return leftNumeric ? -1 : 1;
        return leftIdentifier.localeCompare(rightIdentifier);
    }
    return 0;
}
function provisionVersionCore(version) {
    return `${version.major}.${version.minor}.${version.patch}`;
}
function parseProvisionVersionRange(range) {
    if (range.trim().length === 0)
        return null;
    const alternatives = [];
    for (const rawAlternative of range.split('||')) {
        const alternative = rawAlternative.trim();
        if (alternative.length === 0)
            return null;
        const hyphen = /^(\S+)\s+-\s+(\S+)$/.exec(alternative);
        if (hyphen !== null) {
            const lower = parseProvisionVersion(hyphen[1] ?? '');
            const upper = parseProvisionVersion(hyphen[2] ?? '');
            if (lower === null || upper === null)
                return null;
            alternatives.push({
                comparators: [
                    (candidate) => compareProvisionVersions(candidate, lower) >= 0,
                    (candidate) => compareProvisionVersions(candidate, upper) <= 0,
                ],
                prereleaseCores: new Set([lower, upper]
                    .filter((version) => version.prerelease.length > 0)
                    .map(provisionVersionCore)),
            });
            continue;
        }
        const comparators = [];
        const prereleaseCores = new Set();
        for (const rawComparator of alternative.split(/\s+/)) {
            const comparator = parseProvisionComparator(rawComparator);
            if (comparator === null)
                return null;
            comparators.push(...comparator.comparators);
            comparator.prereleaseCores.forEach((core) => prereleaseCores.add(core));
        }
        if (comparators.length === 0)
            return null;
        alternatives.push({ comparators, prereleaseCores });
    }
    return alternatives;
}
function parseProvisionComparator(raw) {
    if (raw === '*' || raw.toLowerCase() === 'x') {
        return { comparators: [() => true], prereleaseCores: new Set() };
    }
    const wildcard = /^(0|[1-9]\d*)\.(?:(x|X|\*)|(0|[1-9]\d*)\.(x|X|\*))$/.exec(raw);
    if (wildcard !== null) {
        const major = Number(wildcard[1]);
        if (wildcard[2] !== undefined) {
            const lower = provisionRelease(major, 0, 0);
            const upper = provisionRelease(major + 1, 0, 0);
            return {
                comparators: [
                    (candidate) => compareProvisionVersions(candidate, lower) >= 0,
                    (candidate) => compareProvisionVersions(candidate, upper) < 0,
                ],
                prereleaseCores: new Set(),
            };
        }
        const minor = Number(wildcard[3]);
        const lower = provisionRelease(major, minor, 0);
        const upper = provisionRelease(major, minor + 1, 0);
        return {
            comparators: [
                (candidate) => compareProvisionVersions(candidate, lower) >= 0,
                (candidate) => compareProvisionVersions(candidate, upper) < 0,
            ],
            prereleaseCores: new Set(),
        };
    }
    if (raw.startsWith('^')) {
        const base = parseProvisionVersion(raw.slice(1));
        if (base === null)
            return null;
        const upper = base.major > 0
            ? provisionRelease(base.major + 1, 0, 0)
            : base.minor > 0
                ? provisionRelease(0, base.minor + 1, 0)
                : provisionRelease(0, 0, base.patch + 1);
        return {
            comparators: [
                (candidate) => compareProvisionVersions(candidate, base) >= 0,
                (candidate) => compareProvisionVersions(candidate, upper) < 0,
            ],
            prereleaseCores: new Set(base.prerelease.length > 0 ? [provisionVersionCore(base)] : []),
        };
    }
    if (raw.startsWith('~')) {
        const base = parseProvisionVersion(raw.slice(1));
        if (base === null)
            return null;
        const upper = provisionRelease(base.major, base.minor + 1, 0);
        return {
            comparators: [
                (candidate) => compareProvisionVersions(candidate, base) >= 0,
                (candidate) => compareProvisionVersions(candidate, upper) < 0,
            ],
            prereleaseCores: new Set(base.prerelease.length > 0 ? [provisionVersionCore(base)] : []),
        };
    }
    const match = /^(>=|<=|>|<|=)?(.+)$/.exec(raw);
    if (match === null)
        return null;
    const target = parseProvisionVersion(match[2] ?? '');
    if (target === null)
        return null;
    const prereleaseCores = new Set(target.prerelease.length > 0 ? [provisionVersionCore(target)] : []);
    switch (match[1] ?? '=') {
        case '>=':
            return {
                comparators: [(candidate) => compareProvisionVersions(candidate, target) >= 0],
                prereleaseCores,
            };
        case '<=':
            return {
                comparators: [(candidate) => compareProvisionVersions(candidate, target) <= 0],
                prereleaseCores,
            };
        case '>':
            return {
                comparators: [(candidate) => compareProvisionVersions(candidate, target) > 0],
                prereleaseCores,
            };
        case '<':
            return {
                comparators: [(candidate) => compareProvisionVersions(candidate, target) < 0],
                prereleaseCores,
            };
        default:
            return {
                comparators: [(candidate) => compareProvisionVersions(candidate, target) === 0],
                prereleaseCores,
            };
    }
}
function provisionRelease(major, minor, patch) {
    return { major, minor, patch, prerelease: [] };
}
//# sourceMappingURL=provision-composition.js.map